import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import axios from 'axios';
import {
  Plus,
  Settings,
  Filter,
  LayoutGrid,
  List,
  Download,
  MoreVertical,
  Search,
  SlidersHorizontal,
  RefreshCw,
  FileDown,
  Archive,
  X,
  ArrowUp,
  ArrowDown,
  Check,
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Badge } from '../../../components/ui/badge';
import { Label } from '../../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from '../../../components/ui/sheet';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../../components/ui/tooltip';
import { cn } from '../../../lib/utils';
import KanbanBoard from '../../../components/opportunities/KanbanBoard';
import OpportunityListView from '../../../components/opportunities/OpportunityListView';
import PipelinesTab from '../../../components/opportunities/PipelinesTab';
import BulkActionsTab from '../../../components/opportunities/BulkActionsTab';
import AddOpportunityModal from '../../../components/opportunities/AddOpportunityModal';

// Normalize backend URL to prevent double /api paths
const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;

const SORT_OPTIONS = [
  { value: 'created_at_desc', label: 'Newest First', icon: ArrowDown },
  { value: 'created_at_asc', label: 'Oldest First', icon: ArrowUp },
  { value: 'value_desc', label: 'Value: High to Low', icon: ArrowDown },
  { value: 'value_asc', label: 'Value: Low to High', icon: ArrowUp },
  { value: 'last_activity_desc', label: 'Last Activity', icon: ArrowDown },
  { value: 'title_asc', label: 'Contact Name A-Z', icon: ArrowUp },
];

const STATUS_OPTIONS = [
  { value: 'all', label: 'All Statuses' },
  { value: 'open', label: 'Open' },
  { value: 'won', label: 'Won' },
  { value: 'lost', label: 'Lost' },
  { value: 'review', label: 'Review' },
  { value: 'archived', label: 'Archived' },
];

const SOURCE_OPTIONS = [
  { value: 'all', label: 'All Sources' },
  { value: 'whop', label: 'Whop' },
  { value: 'manual', label: 'Manual' },
  { value: 'import', label: 'Import' },
  { value: 'automation', label: 'Automation' },
];

const AgencyOpportunitiesPage = () => {
  const { agencyId } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // State
  const [activeTab, setActiveTab] = useState('opportunities');
  const [viewMode, setViewMode] = useState(searchParams.get('view') || 'board');
  const [pipelines, setPipelines] = useState([]);
  const [opportunities, setOpportunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPipelineId, setSelectedPipelineId] = useState(searchParams.get('pipeline') || null);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [selectedIds, setSelectedIds] = useState([]);
  const [sortBy, setSortBy] = useState(searchParams.get('sort') || 'created_at_desc');
  
  // Filter state
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'all');
  const [sourceFilter, setSourceFilter] = useState(searchParams.get('source') || 'all');
  const [stageFilter, setStageFilter] = useState(searchParams.get('stage') || 'all');
  const [valueMin, setValueMin] = useState(searchParams.get('valueMin') || '');
  const [valueMax, setValueMax] = useState(searchParams.get('valueMax') || '');
  
  // Modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [showFiltersSheet, setShowFiltersSheet] = useState(false);
  const [showSortDropdown, setShowSortDropdown] = useState(false);

  // Fetch data on mount
  useEffect(() => {
    fetchData();
  }, [token, agencyId]);

  // Update URL params when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    if (selectedPipelineId) params.set('pipeline', selectedPipelineId);
    if (viewMode !== 'board') params.set('view', viewMode);
    if (searchQuery) params.set('search', searchQuery);
    if (sortBy !== 'created_at_desc') params.set('sort', sortBy);
    if (statusFilter !== 'all') params.set('status', statusFilter);
    if (sourceFilter !== 'all') params.set('source', sourceFilter);
    setSearchParams(params, { replace: true });
  }, [selectedPipelineId, viewMode, searchQuery, sortBy, statusFilter, sourceFilter]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const headers = {
        Authorization: `Bearer ${token}`,
        'X-Agency-ID': agencyId,
      };

      const [pipelinesRes, oppsRes] = await Promise.all([
        axios.get(`${API}/agency/pipelines`, { headers }),
        axios.get(`${API}/agency/opportunities?limit=500`, { headers }),
      ]);
      
      const fetchedPipelines = pipelinesRes.data.pipelines || [];
      setPipelines(fetchedPipelines);
      setOpportunities(oppsRes.data.opportunities || []);
      
      // Select pipeline from URL or first one
      const urlPipeline = searchParams.get('pipeline');
      if (urlPipeline && fetchedPipelines.find(p => p.id === urlPipeline)) {
        setSelectedPipelineId(urlPipeline);
      } else if (!selectedPipelineId && fetchedPipelines.length > 0) {
        setSelectedPipelineId(fetchedPipelines[0].id);
      }
    } catch (err) {
      console.error('Failed to fetch opportunities data:', err);
    } finally {
      setLoading(false);
    }
  };

  // Get selected pipeline
  const selectedPipeline = pipelines.find(p => p.id === selectedPipelineId);
  const stages = selectedPipeline?.stages || [];
  const colorMode = selectedPipeline?.stage_color_mode || 'colored_dot';

  // Filter and sort opportunities
  const filteredOpportunities = useMemo(() => {
    let result = [...opportunities];
    
    // Filter by pipeline
    if (selectedPipelineId) {
      result = result.filter(opp => opp.pipeline_id === selectedPipelineId);
    }
    
    // Filter by status
    if (statusFilter !== 'all') {
      result = result.filter(opp => opp.status === statusFilter);
    }
    
    // Filter by source
    if (sourceFilter !== 'all') {
      result = result.filter(opp => opp.source === sourceFilter);
    }
    
    // Filter by stage
    if (stageFilter !== 'all') {
      result = result.filter(opp => opp.stage_id === stageFilter);
    }
    
    // Filter by value range
    if (valueMin) {
      result = result.filter(opp => (opp.value || 0) >= parseFloat(valueMin));
    }
    if (valueMax) {
      result = result.filter(opp => (opp.value || 0) <= parseFloat(valueMax));
    }
    
    // Search
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(opp =>
        opp.title?.toLowerCase().includes(q) ||
        opp.contact_name?.toLowerCase().includes(q) ||
        opp.email?.toLowerCase().includes(q) ||
        opp.source?.toLowerCase().includes(q)
      );
    }
    
    // Sort
    const [sortField, sortDir] = sortBy.split('_');
    result.sort((a, b) => {
      let aVal, bVal;
      if (sortField === 'created' || sortField === 'created_at') {
        aVal = new Date(a.created_at || 0).getTime();
        bVal = new Date(b.created_at || 0).getTime();
      } else if (sortField === 'value') {
        aVal = a.value || 0;
        bVal = b.value || 0;
      } else if (sortField === 'last' || sortField === 'last_activity') {
        aVal = new Date(a.last_activity_at || a.created_at || 0).getTime();
        bVal = new Date(b.last_activity_at || b.created_at || 0).getTime();
      } else if (sortField === 'title') {
        aVal = (a.contact_name || a.title || '').toLowerCase();
        bVal = (b.contact_name || b.title || '').toLowerCase();
        return sortDir === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }
      return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
    });
    
    return result;
  }, [opportunities, selectedPipelineId, statusFilter, sourceFilter, stageFilter, valueMin, valueMax, searchQuery, sortBy]);

  const totalCount = filteredOpportunities.length;
  const openCount = filteredOpportunities.filter(o => o.status === 'open').length;

  // Handle stage move
  const handleMoveOpportunity = useCallback(async (opportunityId, oldStageId, newStageId) => {
    // Optimistic update
    setOpportunities(prev => prev.map(opp => 
      opp.id === opportunityId 
        ? { ...opp, stage_id: newStageId }
        : opp
    ));

    try {
      await axios.patch(
        `${API}/agency/opportunities/${opportunityId}`,
        { 
          stage_id: newStageId,
          old_stage_id: oldStageId,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'X-Agency-ID': agencyId,
          },
        }
      );
    } catch (err) {
      console.error('Failed to move opportunity:', err);
      // Rollback on error
      setOpportunities(prev => prev.map(opp => 
        opp.id === opportunityId 
          ? { ...opp, stage_id: oldStageId }
          : opp
      ));
    }
  }, [token, agencyId]);

  // Handle successful opportunity creation
  const handleOpportunityCreated = (newOpportunity) => {
    setOpportunities(prev => [newOpportunity, ...prev]);
  };

  // Selection handlers
  const handleSelectOne = (id) => {
    setSelectedIds(prev => 
      prev.includes(id) 
        ? prev.filter(i => i !== id)
        : [...prev, id]
    );
  };

  const handleSelectAll = (select) => {
    setSelectedIds(select ? filteredOpportunities.map(o => o.id) : []);
  };

  // Clear all filters
  const clearFilters = () => {
    setStatusFilter('all');
    setSourceFilter('all');
    setStageFilter('all');
    setValueMin('');
    setValueMax('');
    setSearchQuery('');
  };

  const hasActiveFilters = statusFilter !== 'all' || sourceFilter !== 'all' || stageFilter !== 'all' || valueMin || valueMax;

  return (
    <div className="space-y-4" data-testid="agency-opportunities-page">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-stone-900 dark:text-white">Opportunities</h1>
      </div>

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="flex items-center justify-between border-b border-slate-200 dark:border-stone-700">
          <TabsList className="bg-transparent border-0 p-0 h-auto">
            <TabsTrigger 
              value="opportunities" 
              className="px-4 py-2.5 text-sm font-medium data-[state=active]:text-emerald-600 data-[state=active]:border-b-2 data-[state=active]:border-emerald-600 rounded-none bg-transparent"
            >
              Opportunities
            </TabsTrigger>
            <TabsTrigger 
              value="pipelines"
              className="px-4 py-2.5 text-sm font-medium data-[state=active]:text-emerald-600 data-[state=active]:border-b-2 data-[state=active]:border-emerald-600 rounded-none bg-transparent"
            >
              Pipelines
            </TabsTrigger>
            <TabsTrigger 
              value="bulk-actions"
              className="px-4 py-2.5 text-sm font-medium data-[state=active]:text-emerald-600 data-[state=active]:border-b-2 data-[state=active]:border-emerald-600 rounded-none bg-transparent"
            >
              Bulk Actions
            </TabsTrigger>
          </TabsList>
        </div>

        {/* Opportunities Tab Content */}
        <TabsContent value="opportunities" className="mt-4 space-y-4">
          {/* Controls Row 1 */}
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              {/* Pipeline Selector */}
              <Select 
                value={selectedPipelineId || ''} 
                onValueChange={(value) => {
                  setSelectedPipelineId(value);
                  setStageFilter('all');
                }}
              >
                <SelectTrigger className="w-56 dark:bg-stone-800 dark:border-stone-700" data-testid="pipeline-dropdown">
                  <SelectValue placeholder="Select Pipeline" />
                </SelectTrigger>
                <SelectContent>
                  {pipelines.map((pipeline) => (
                    <SelectItem key={pipeline.id} value={pipeline.id}>
                      {pipeline.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Count Badge */}
              <Badge variant="outline" className="text-emerald-600 border-emerald-300 bg-emerald-50 dark:bg-emerald-900/20 dark:border-emerald-800">
                {totalCount} opportunit{totalCount === 1 ? 'y' : 'ies'}
              </Badge>
            </div>

            <div className="flex items-center gap-2">
              {/* View Toggle */}
              <div className="flex items-center border border-slate-200 rounded-lg overflow-hidden dark:border-stone-700">
                <button
                  onClick={() => setViewMode('board')}
                  className={cn(
                    'p-2 transition-colors',
                    viewMode === 'board' 
                      ? 'bg-slate-100 text-stone-900 dark:bg-stone-700 dark:text-white' 
                      : 'text-slate-500 hover:bg-slate-50 dark:hover:bg-stone-800'
                  )}
                  data-testid="view-toggle-board"
                  title="Board View"
                >
                  <LayoutGrid className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={cn(
                    'p-2 transition-colors',
                    viewMode === 'list' 
                      ? 'bg-slate-100 text-stone-900 dark:bg-stone-700 dark:text-white' 
                      : 'text-slate-500 hover:bg-slate-50 dark:hover:bg-stone-800'
                  )}
                  data-testid="view-toggle-list"
                  title="List View"
                >
                  <List className="h-4 w-4" />
                </button>
              </div>

              {/* Import Button - Disabled */}
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button variant="outline" size="sm" disabled className="opacity-50 dark:border-stone-700">
                      <Download className="mr-2 h-4 w-4" />
                      Import
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Import opportunities coming in Phase 2</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>

              {/* Add Opportunity Button */}
              <Button 
                size="sm" 
                className="bg-emerald-600 hover:bg-emerald-700"
                onClick={() => setShowAddModal(true)}
                data-testid="add-opportunity-btn"
              >
                <Plus className="mr-2 h-4 w-4" />
                Add opportunity
              </Button>

              {/* More Menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="h-9 w-9 p-0" data-testid="more-menu-btn">
                    <MoreVertical className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" data-testid="more-menu-content">
                  <DropdownMenuItem onClick={fetchData}>
                    <RefreshCw className="mr-2 h-4 w-4" />
                    Refresh Board
                  </DropdownMenuItem>
                  <DropdownMenuItem disabled>
                    <FileDown className="mr-2 h-4 w-4" />
                    Export Opportunities
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => setActiveTab('pipelines')}>
                    <Settings className="mr-2 h-4 w-4" />
                    Manage Pipelines
                  </DropdownMenuItem>
                  <DropdownMenuItem disabled>
                    <Archive className="mr-2 h-4 w-4" />
                    View Archived
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          {/* Controls Row 2 */}
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              {/* Status quick filters */}
              <button 
                onClick={() => setStatusFilter(statusFilter === 'open' ? 'all' : 'open')}
                className={cn(
                  'flex items-center gap-2 text-sm transition-colors px-2 py-1 rounded',
                  statusFilter === 'open' 
                    ? 'text-emerald-700 bg-emerald-50 dark:text-emerald-400 dark:bg-emerald-900/30' 
                    : 'text-stone-700 hover:text-stone-900 hover:bg-slate-50 dark:text-stone-300 dark:hover:bg-stone-800'
                )}
                data-testid="open-opportunities-filter"
              >
                <List className="h-4 w-4" />
                Open opportunities ({openCount})
              </button>
              <span className="text-slate-300 dark:text-stone-600">|</span>
              <button 
                onClick={() => setViewMode('list')}
                className="text-sm text-slate-500 hover:text-stone-700 dark:hover:text-stone-300"
              >
                List
              </button>
            </div>

            <div className="flex items-center gap-3">
              {/* Advanced Filters */}
              <Button 
                variant="outline" 
                size="sm" 
                className={cn(
                  'dark:border-stone-700',
                  hasActiveFilters && 'text-emerald-600 border-emerald-300 bg-emerald-50 dark:bg-emerald-900/30'
                )}
                onClick={() => setShowFiltersSheet(true)}
                data-testid="advanced-filters-btn"
              >
                <Filter className="mr-2 h-4 w-4" />
                Advanced Filters
                {hasActiveFilters && (
                  <Badge className="ml-2 bg-emerald-600 text-white text-xs px-1.5">
                    {[statusFilter !== 'all', sourceFilter !== 'all', stageFilter !== 'all', valueMin, valueMax].filter(Boolean).length}
                  </Badge>
                )}
              </Button>

              {/* Sort Dropdown */}
              <DropdownMenu open={showSortDropdown} onOpenChange={setShowSortDropdown}>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="dark:border-stone-700" data-testid="sort-btn">
                    <SlidersHorizontal className="mr-2 h-4 w-4" />
                    Sort
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48" data-testid="sort-dropdown">
                  {SORT_OPTIONS.map((option) => {
                    const Icon = option.icon;
                    return (
                      <DropdownMenuItem 
                        key={option.value}
                        onClick={() => {
                          setSortBy(option.value);
                          setShowSortDropdown(false);
                        }}
                        className={cn(sortBy === option.value && 'bg-emerald-50 dark:bg-emerald-900/30')}
                      >
                        <Icon className="mr-2 h-4 w-4" />
                        {option.label}
                        {sortBy === option.value && <Check className="ml-auto h-4 w-4 text-emerald-600" />}
                      </DropdownMenuItem>
                    );
                  })}
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search Opportunities"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 w-56 dark:bg-stone-800 dark:border-stone-700"
                  data-testid="search-input"
                />
                {searchQuery && (
                  <button 
                    onClick={() => setSearchQuery('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>

              {/* Manage Fields - Disabled */}
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button variant="outline" size="sm" disabled className="opacity-50 dark:border-stone-700">
                      <Settings className="mr-2 h-4 w-4" />
                      Manage Fields
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Manage Fields coming in Phase 2</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>

          {/* Board or List View */}
          {viewMode === 'board' ? (
            <KanbanBoard
              stages={stages}
              opportunities={filteredOpportunities}
              colorMode={colorMode}
              onMoveOpportunity={handleMoveOpportunity}
              loading={loading}
            />
          ) : (
            <OpportunityListView
              opportunities={filteredOpportunities}
              stages={stages}
              selectedIds={selectedIds}
              onSelectOne={handleSelectOne}
              onSelectAll={handleSelectAll}
              loading={loading}
            />
          )}
        </TabsContent>

        {/* Pipelines Tab Content */}
        <TabsContent value="pipelines" className="mt-4">
          <PipelinesTab
            token={token}
            agencyId={agencyId}
            isAgency={true}
            basePath={`/agency/${agencyId}/opportunities`}
          />
        </TabsContent>

        {/* Bulk Actions Tab Content */}
        <TabsContent value="bulk-actions" className="mt-4">
          <BulkActionsTab
            token={token}
            agencyId={agencyId}
            isAgency={true}
          />
        </TabsContent>
      </Tabs>

      {/* Add Opportunity Modal */}
      <AddOpportunityModal
        open={showAddModal}
        onOpenChange={setShowAddModal}
        token={token}
        agencyId={agencyId}
        isAgency={true}
        pipelines={pipelines}
        defaultPipelineId={selectedPipelineId}
        onSuccess={handleOpportunityCreated}
      />

      {/* Advanced Filters Sheet */}
      <Sheet open={showFiltersSheet} onOpenChange={setShowFiltersSheet}>
        <SheetContent data-testid="filters-sheet">
          <SheetHeader>
            <SheetTitle>Advanced Filters</SheetTitle>
            <SheetDescription>
              Filter opportunities by multiple criteria
            </SheetDescription>
          </SheetHeader>
          
          <div className="space-y-6 py-6">
            {/* Status */}
            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Source */}
            <div className="space-y-2">
              <Label>Source</Label>
              <Select value={sourceFilter} onValueChange={setSourceFilter}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SOURCE_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Stage */}
            <div className="space-y-2">
              <Label>Stage</Label>
              <Select value={stageFilter} onValueChange={setStageFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="All Stages" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Stages</SelectItem>
                  {stages.map((stage) => (
                    <SelectItem key={stage.id} value={stage.id}>
                      <div className="flex items-center gap-2">
                        {stage.color && (
                          <span className="w-2 h-2 rounded-full" style={{ backgroundColor: stage.color }} />
                        )}
                        {stage.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Value Range */}
            <div className="space-y-2">
              <Label>Value Range</Label>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  placeholder="Min"
                  value={valueMin}
                  onChange={(e) => setValueMin(e.target.value)}
                  className="w-full"
                />
                <span className="text-slate-400">-</span>
                <Input
                  type="number"
                  placeholder="Max"
                  value={valueMax}
                  onChange={(e) => setValueMax(e.target.value)}
                  className="w-full"
                />
              </div>
            </div>
          </div>
          
          <SheetFooter>
            <Button variant="outline" onClick={clearFilters}>
              Clear All
            </Button>
            <Button onClick={() => setShowFiltersSheet(false)} className="bg-emerald-600 hover:bg-emerald-700">
              Apply Filters
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default AgencyOpportunitiesPage;
